import { buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import type { DailyHistorySortKey } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type { QueryFilter, QuerySort, SuiteRunStatus } from '@flowqa/shared';

type BuildSuiteHistoryQueryStateKeyInput = {
  panelDateQuickFilter: string;
  panelDateWindowEndDay: string | null;
  panelDateWindowStartDay: string | null;
  panelSearchValue: string;
  panelFilterStatuses: SuiteRunStatus[];
  panelFilterSuites: string[];
  panelFilterTags: string[];
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  historyPage: number;
  historyPageSize: number;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

type BuildSuiteHistoryQueryInputArgs = {
  panelDateWindowEndDay: string | null;
  panelDateWindowStartDay: string | null;
  panelSearchValue: string;
  panelFilterStatuses: SuiteRunStatus[];
  panelFilterSuites: string[];
  panelFilterTags: string[];
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  historyPage: number;
  historyPageSize: number;
};

type SuiteHistoryQueryInput = {
  page: number;
  pageSize: number;
  sorts: QuerySort[];
  filters: QueryFilter[];
  search?: string;
  metricsStartDay?: string;
  metricsEndDay?: string;
  include: {
    dailyHistory: true;
    latestTestResults: { counts: true };
  };
};

export const buildSuiteHistoryQueryStateKey = (
  input: BuildSuiteHistoryQueryStateKeyInput,
): string =>
  JSON.stringify({
    panelDateQuickFilter: input.panelDateQuickFilter,
    panelSearchValue: input.panelSearchValue,
    panelFilterStatuses: input.panelFilterStatuses,
    panelFilterSuites: input.panelFilterSuites,
    panelFilterTags: input.panelFilterTags,
    historySortDirection: input.historySortDirection,
    historySortKey: input.historySortKey,
    panelDateWindowStartDay: input.panelDateWindowStartDay,
    panelDateWindowEndDay: input.panelDateWindowEndDay,
    historyPage: input.historyPage,
    historyPageSize: input.historyPageSize,
    selectedOrganisationId: input.selectedOrganisationId,
    selectedProjectId: input.selectedProjectId,
  });

const buildSuiteHistoryFilters = (
  panelFilterStatuses: SuiteRunStatus[],
  panelFilterSuites: string[],
  panelFilterTags: string[],
): QueryFilter[] =>
  mergeFilters(
    buildInFilter('executionStatus', panelFilterStatuses),
    buildInFilter('id', panelFilterSuites),
    buildInFilter('tags', panelFilterTags),
  );

export const buildSuiteHistoryQueryInput = (
  args: BuildSuiteHistoryQueryInputArgs,
): SuiteHistoryQueryInput => ({
  page: args.historyPage + 1,
  pageSize: args.historyPageSize,
  search: args.panelSearchValue.trim(),
  sorts: [{ field: args.historySortKey, direction: args.historySortDirection }],
  filters: buildSuiteHistoryFilters(
    args.panelFilterStatuses,
    args.panelFilterSuites,
    args.panelFilterTags,
  ),
  metricsStartDay: args.panelDateWindowStartDay ?? undefined,
  metricsEndDay: args.panelDateWindowEndDay ?? undefined,
  include: {
    dailyHistory: true,
    latestTestResults: { counts: true },
  },
});
